import type { ConsentCollectionSnapshot } from '../types/consent';

const STORAGE_KEY = 'melio.enrollmentDraft.v1';

export interface EnrollmentDraftForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  message: string;
}

export interface EnrollmentDraft {
  form: EnrollmentDraftForm;
  selectedOptionId: string | null;
  consents: ConsentCollectionSnapshot;
  updatedAt: string;
}

const EMPTY_FORM: EnrollmentDraftForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  message: '',
};

const EMPTY_CONSENTS: ConsentCollectionSnapshot = {
  terms: false,
  privacy: false,
  healthDataProcessing: false,
  geneticsContext: false,
  geneticDataRetention: false,
};

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readEnrollmentDraft(): EnrollmentDraft | null {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EnrollmentDraft;
    if (!parsed?.form) return null;
    return {
      form: { ...EMPTY_FORM, ...parsed.form },
      selectedOptionId: parsed.selectedOptionId ?? null,
      consents: { ...EMPTY_CONSENTS, ...(parsed.consents || {}) },
      updatedAt: parsed.updatedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeEnrollmentDraft(draft: Omit<EnrollmentDraft, 'updatedAt'>): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...draft,
        updatedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // Quota or private browsing — non-fatal.
  }
}

export function clearEnrollmentDraft(): void {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(STORAGE_KEY);
}
